import { useContext, useRef, useMemo, useState, useCallback, useLayoutEffect, useEffect } from 'react';
import { prepareUrlParams, resolvePath, isFetchable } from './utils.js';
import RequestContext from './context.js';
import fetchResource from './fetch.js';

function useDidUpdateEffect(fn, inputs) {
  const didMountRef = useRef(false);
  useEffect(() => {
    if (didMountRef.current) {
      return fn();
    }
    didMountRef.current = true;
    return undefined;
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  inputs);
}
function useRequest(requestOptions) {
  const {
    transformOptions,
    transformUrl,
    transformResponse,
    transformError,
    getCache,
    setCache
  } = useContext(RequestContext);

  // NOTE: forgot why the clientId is required but it is required
  const clientIdRef = useRef(-1);
  const pendingSetByRef = useRef(-1);
  const responseSetByRef = useRef(-1);
  const errorSetByRef = useRef(-1);

  // NOTE: let's not add transformOptions as dependency
  const requestOptionsRef = useRef(requestOptions);
  const transformOptionsRef = useRef(transformOptions);
  const transformUrlRef = useRef(transformUrl);
  const transformResponseRef = useRef(transformResponse);
  const transformErrorRef = useRef(transformError);
  const getCacheRef = useRef(getCache);
  const setCacheRef = useRef(setCache);
  const {
    skip = false
  } = requestOptions;
  const {
    url,
    query,
    method = 'GET',
    body,
    other,
    pathVariables
  } = requestOptions;
  const extendedUrl = useMemo(() => {
    if (skip) {
      return undefined;
    }
    const urlQuery = query ? prepareUrlParams(query) : undefined;
    const middleUrl = url && urlQuery ? `${url}?${urlQuery}` : url;
    return middleUrl ? resolvePath(middleUrl, pathVariables) : url;
  }, [pathVariables, url, query, skip]);
  const [response, setResponse] = useState();
  const [error, setError] = useState();
  const [runId, setRunId] = useState(() => skip ? -1 : new Date().getTime());
  const [pending, setPending] = useState(() => runId >= 0 && isFetchable(extendedUrl, method, body));
  const setPendingSafe = useCallback((value, clientId) => {
    if (clientId >= pendingSetByRef.current) {
      pendingSetByRef.current = clientId;
      setPending(value);
    }
  }, []);
  const setResponseSafe = useCallback((value, clientId) => {
    if (clientId >= responseSetByRef.current) {
      responseSetByRef.current = clientId;
      setResponse(value);
    }
  }, []);
  const setErrorSafe = useCallback((value, clientId) => {
    if (clientId >= errorSetByRef.current) {
      errorSetByRef.current = clientId;
      setError(value);
    }
  }, []);
  const callSideEffectSafe = useCallback((callback, clientId) => {
    if (clientId >= clientIdRef.current) {
      callback();
    }
  }, []);
  useLayoutEffect(() => {
    transformOptionsRef.current = transformOptions;
  }, [transformOptions]);
  useLayoutEffect(() => {
    transformUrlRef.current = transformUrl;
  }, [transformUrl]);
  useLayoutEffect(() => {
    transformResponseRef.current = transformResponse;
  }, [transformResponse]);
  useLayoutEffect(() => {
    transformErrorRef.current = transformError;
  }, [transformError]);
  useLayoutEffect(() => {
    requestOptionsRef.current = requestOptions;
  }, [requestOptions]);

  // To re-trigger request when skip changes
  useDidUpdateEffect(() => {
    setRunId(skip ? -1 : new Date().getTime());
  }, [skip]);
  useEffect(() => {
    if (runId < 0 || !isFetchable(extendedUrl, method, body)) {
      setResponseSafe(undefined, clientIdRef.current);
      setErrorSafe(undefined, clientIdRef.current);
      setPendingSafe(false, clientIdRef.current);
      return undefined;
    }
    const {
      preserveResponse,
      delay = 0
    } = requestOptionsRef.current;
    const previousCache = getCacheRef.current ? getCacheRef.current(extendedUrl) : undefined;
    if (method === 'GET' && previousCache) {
      setResponseSafe(previousCache, clientIdRef.current);
      setErrorSafe(undefined, clientIdRef.current);
    } else if (!preserveResponse) {
      setResponseSafe(undefined, clientIdRef.current);
      setErrorSafe(undefined, clientIdRef.current);
    }
    clientIdRef.current += 1;

    // FIXME: this may need to move up
    setPendingSafe(true, clientIdRef.current);
    const controller = new AbortController();
    fetchResource(extendedUrl, {
      ...other,
      method,
      // FIXME: here object is explicitly cast as BodyInit
      body: body
    }, delay, transformUrlRef, transformOptionsRef, transformResponseRef, transformErrorRef, setCacheRef, requestOptionsRef, null, setPendingSafe, setResponseSafe, setErrorSafe, callSideEffectSafe, controller, clientIdRef.current);
    return () => {
      controller.abort();
    };
  }, [extendedUrl, method, body, other, setPendingSafe, setResponseSafe, setErrorSafe, callSideEffectSafe, runId]);
  const retrigger = useCallback(() => {
    setRunId(skip ? -1 : new Date().getTime());
  }, [skip]);
  return {
    response,
    pending,
    error,
    retrigger
  };
}

export { useRequest as default };
//# sourceMappingURL=useRequest.js.map
